import { useCanManageEmployees } from 'utils/useCanManageEmployees';
import { Alert, Box, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import Toolbar from 'components/@home/@people/PeoplesTable/Toolbar';
import EmployeeAvatar from 'components/@home/EmployeeAvatar';
import { useConfirm } from 'components/common/GlobalConfirmDialog';
import OmmnioDataGrid from 'components/common/OmmnioDataGrid';
import DeleteIcon from 'mdi-react/DeleteIcon';
import EditIcon from 'mdi-react/EditIcon';
import IncognitoIcon from 'mdi-react/IncognitoIcon';
import LockIcon from 'mdi-react/LockIcon';
import LockOpenIcon from 'mdi-react/LockOpenIcon';
import ShieldAccountIcon from 'mdi-react/ShieldAccountIcon';
import { bool, func, object } from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ImpersonationStepUpDialog from 'components/common/ImpersonationStepUpDialog';
import { useNavigate } from 'react-router';
import { STATUS_ACTIVE } from 'store/app/entities/employees/reducer';
import {
  useBlockMutation,
  useDeleteMutation,
  useListQuery,
} from 'store/app/entities/EmployeesApiSlice';
import authCompany from 'store/selectors/authCompany';
import { I18n } from 'utils/i18n';
import { translateSearchKeywords } from 'utils/searchKeywords';
import { useDebounceValue } from 'utils/useDebounceValue';
import useQuery from 'utils/useQuery';

const defaultSortModel = [{ field: 'name', sort: 'asc' }];

const PeoplesTable = ({
  onOpen,
  onEdit,
  additionalControls = null,
  isSuperAdmin,
  showSessions,
}) => {
  const canManageEmployees = useCanManageEmployees();

  const [sortModel, setSortModel] = useState(defaultSortModel);
  const [filterModel, setFilterModel] = useState(undefined);
  const { p = 0 } = useQuery();
  const [page, setPage] = useState(p);
  const [rowsPerPage, setRowsPerPage] = useState(-1);
  const [debouncedFilterModel] = useDebounceValue(filterModel, 500);
  const [debouncedRowsPerPage] = useDebounceValue(rowsPerPage, 500);
  const [block] = useBlockMutation();
  const [remove] = useDeleteMutation();
  const { open: openConfirm, close: closeConfirm } = useConfirm();
  const navigate = useNavigate();
  const [impersonationTarget, setImpersonationTarget] = useState(null);
  const confirmDelete = useCallback(
    employeeToDelete =>
      openConfirm({
        message: I18n.t('PeoplesScene.ConfirmDelete', {
          name: `${employeeToDelete?.firstName} ${employeeToDelete?.surName}`.trim(),
        }),
        isDelete: true,
        typeBack: employeeToDelete?.surName?.trim(),
        children: (
          <Alert severity="warning">
            <Typography
              sx={{
                display: 'inline',
              }}
            >
              {I18n.t('PeoplesScene.DeleteWarning')}
            </Typography>
            <Box component="span" sx={{ position: 'relative' }}>
              <LockOpenIcon style={{ position: 'absolute' }} size={18} />
            </Box>
          </Alert>
        ),
        onConfirm: () => {
          closeConfirm();
          remove({ _id: employeeToDelete._id });
        },
      }),
    [closeConfirm, openConfirm, remove],
  );
  const company = useSelector(authCompany);
  const { data, isFetching, isLoading, isUninitialized } = useListQuery(
    {
      companyId: company._id,
      search: translateSearchKeywords(debouncedFilterModel?.quickFilterValues.join(' ') || ''),
      skip: page * debouncedRowsPerPage,
      limit: debouncedRowsPerPage,
      filters: (filterModel?.items || []).reduce(
        (acc, { field, value }) => ({
          ...acc,
          [field]: value,
        }),
        {},
      ),
      order: sortModel[0]
        ? `${sortModel[0].sort === 'desc' ? '-' : ''}${sortModel[0].field}`
        : 'name',
    },
    { skip: debouncedRowsPerPage < 2 },
  );
  const employees = useMemo(() => data?.data || [], [data?.data]);
  const total = useMemo(() => data?.metadata?.total || 0, [data?.metadata?.total]);

  const columns = useMemo(() => {
    return [
      {
        field: 'status',
        width: 0,
      },
      {
        field: 'name',
        flex: 2,
        headerName: I18n.t('ChannelDrawer.EmployeesTable.Name'),
        valueGetter: (v, { firstName, surName }) => `${firstName} ${surName}`,
        renderCell: ({ row: employee }) => (
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}>
            <Box sx={{ flexShrink: 0 }}>
              <EmployeeAvatar
                status={employee.isAcceptTerms ? 'active' : 'pending'}
                employee={employee}
              />
            </Box>
            <Box
              sx={{
                ml: '10px',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: 0.75,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                component="span"
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${employee.firstName?.trim()} ${employee.surName?.trim()}`.trim()}
              </Box>
              {employee.user?.isApp ? (
                <Box
                  component="span"
                  sx={theme => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexShrink: 0,
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1.4,
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    color: theme.palette.text.secondary,
                    backgroundColor: theme.palette.action.hover,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    padding: '1px 6px',
                  })}
                >
                  App
                </Box>
              ) : null}
            </Box>
          </Box>
        ),
      },
      ...(isSuperAdmin
        ? [
            {
              field: 'phone',
              flex: 1,
              headerName: I18n.t('ChannelDrawer.EmployeesTable.Phone'),
              valueGetter: (v, { user, phone }) => user?.phone || phone,
            },
          ]
        : []),
      {
        field: 'code',
        flex: 1,
        headerName: I18n.t('ChannelDrawer.EmployeesTable.Code'),
        valueGetter: (v, { code, division }) => (division ? `${code} - ${division}` : code),
        renderCell: ({ row: { code, division } }) => (
          <Box
            sx={{
              lineHeight: 1.5,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            {!code || <Box>{code}</Box>}
            {!division || <Box sx={{ color: 'text.secondary' }}>({division})</Box>}
          </Box>
        ),
      },
      {
        field: 'department',
        flex: 1,
        headerName: I18n.t('ChannelDrawer.EmployeesTable.Department'),
      },
      {
        field: 'jobTitle',
        flex: 1,
        headerName: I18n.t('ChannelDrawer.EmployeesTable.Job Position'),
      },
      {
        field: 'location',
        flex: 1,
        headerName: I18n.t('ChannelDrawer.EmployeesTable.Location'),
      },
      {
        field: 'teams',
        flex: 2,
        headerName: I18n.t('ChannelDrawer.EmployeesTable.Teams'),
        valueGetter: (v, { teams }) =>
          teams
            ?.map(({ name }) => name)
            .sort((a, b) => a.localeCompare(b))
            .join(', '),
      },
      {
        field: 'tags',
        flex: 1,
        headerName: I18n.t('ChannelDrawer.EmployeesTable.Tags'),
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 1,
        minWidth: isSuperAdmin ? 228 : 148,
        headerName: '',
        getActions: ({ row: employee }) => [
          ...(isSuperAdmin
            ? [
                <GridActionsCellItem
                  key="impersonate"
                  disabled={!employee.user}
                  icon={<IncognitoIcon />}
                  onClick={() => {
                    if (!employee.user || employee.user.role === 2) return;
                    setImpersonationTarget(employee);
                  }}
                  label=""
                />,
                <GridActionsCellItem
                  key="showSessions"
                  icon={<ShieldAccountIcon />}
                  onClick={() => {
                    showSessions(employee);
                  }}
                  label=""
                />,
              ]
            : []),
          <GridActionsCellItem
            key="block"
            disabled={employee.user?.isApp}
            sx={employee.isActive ? undefined : { color: 'danger' }}
            icon={employee.isActive ? <LockOpenIcon /> : <LockIcon />}
            onClick={() => {
              block({
                _id: employee._id,
                isActive: !employee.isActive,
              });
            }}
            label={I18n.t('ChannelDrawer.EmployeesTable.Buttons.Block')}
          />,
          ...(canManageEmployees
            ? [
                <GridActionsCellItem
                  key="edit"
                  icon={<EditIcon />}
                  onClick={() => {
                    onEdit(employee);
                  }}
                  label={I18n.t('ChannelDrawer.EmployeesTable.Buttons.Edit')}
                />,
                <GridActionsCellItem
                  key="delete"
                  icon={<DeleteIcon />}
                  onClick={() => confirmDelete(employee)}
                  label={I18n.t('ChannelDrawer.EmployeesTable.Buttons.Delete')}
                />,
              ]
            : []),
        ],
      },
    ];
  }, [block, confirmDelete, canManageEmployees, isSuperAdmin, onEdit, showSessions]);

  const onFilterModelChange = useCallback(
    fm => {
      setFilterModel(fm);
      setPage(p);
    },
    [p],
  );

  return (
    <>
      <OmmnioDataGrid
        loading={isFetching || isLoading || isUninitialized}
        sx={theme => ({
          marginTop: 3,
          borderWidth: 0,
          height: 'calc(100% - 32px)',
          width: '100%',
          overflowX: 'auto',
          pt: 1,
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          '& .MuiDataGrid-cell:nth-of-type(1)': {
            paddingLeft: theme.spacing(3),
          },
          '& .MuiDataGrid-columnHeaders': {
            borderTop: '1px solid',
            borderColor: theme.palette.divider,
            '& .MuiDataGrid-columnHeader:nth-of-type(1)': {
              paddingLeft: theme.spacing(3),
            },
          },
          '& .MuiDataGrid-row.pending-row .MuiDataGrid-cell': {
            color: theme.palette.secondary.main,
          },
        })}
        columnHeaderHeight={48}
        columns={columns}
        columnVisibilityModel={{ status: false }}
        rows={employees}
        rowCount={total || 0}
        paginationMode="server"
        getRowId={r => r._id}
        sortingMode="server"
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        onSortModelChange={setSortModel}
        onPageChange={setPage}
        rowHeight={52}
        getRowHeight={() => 52}
        pageSize={rowsPerPage}
        onPageSizeChange={setRowsPerPage}
        updateHistory
        defaultSortModel={defaultSortModel}
        onCellClick={cell => {
          if (cell.field !== 'actions') {
            onOpen(cell.row);
          }
        }}
        slots={{
          toolbar: Toolbar,
        }}
        slotProps={{
          toolbar: {
            total,
            children: additionalControls,
            search: translateSearchKeywords(
              debouncedFilterModel?.quickFilterValues.join(' ') || '',
            ),
            filters: (filterModel?.items || []).reduce(
              (acc, { field, value }) => ({
                ...acc,
                [field]: value,
              }),
              {},
            ),
          },
        }}
        getRowClassName={params => (params.row.status !== STATUS_ACTIVE ? 'pending-row' : '')}
      />
      <ImpersonationStepUpDialog
        open={!!impersonationTarget}
        targetEmployee={impersonationTarget}
        onClose={() => setImpersonationTarget(null)}
        onSuccess={() => {
          setImpersonationTarget(null);
          navigate('/home/messages');
        }}
      />
    </>
  );
};

PeoplesTable.propTypes = {
  onOpen: func.isRequired,
  onEdit: func.isRequired,
  additionalControls: object,
  isSuperAdmin: bool.isRequired,
  showSessions: func.isRequired,
};

export default PeoplesTable;
